import { useAuth } from 'contexts/auth';
import { useSnackbar } from 'contexts/snackbar';
import useCompletedChatMessage from 'hooks/chat/useCompletedChatMessage';
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { Button } from 'shell/components';
import { CHAT_QUERY_OPEN_VALUE, CHAT_QUERY_PARAM } from 'utils/chat';

const ChatCoordinatorContext = createContext(null);

const ChatCoordinator = ({ children }) => {
    const history = useHistory();
    const location = useLocation();
    const { auth } = useAuth();
    const notifiedMessageIds = useRef(new Set());
    const [openAssignmentChatId, setOpenAssignmentChatId] = useState(null);
    const [submissionPathByAssignmentId, setSubmissionPathByAssignmentId] = useState({});
    const { close, message } = useSnackbar();
    const { t } = useTranslation();

    const setSubmissionPath = useCallback((assignmentId, path) => {
        if (!assignmentId || !path) return;

        setSubmissionPathByAssignmentId((current) => ({
            ...current,
            [assignmentId]: path,
        }));
    }, []);

    const value = useMemo(() => {
        return {
            openAssignmentChatId,
            setOpenAssignmentChatId,
            submissionPathByAssignmentId,
            setSubmissionPath,
        };
    }, [openAssignmentChatId, setSubmissionPath, submissionPathByAssignmentId]);

    const getOpenChatPath = useCallback((path) => {
        const [pathname, search = ''] = path.split('?');
        const params = new URLSearchParams(search);
        params.set(CHAT_QUERY_PARAM, CHAT_QUERY_OPEN_VALUE);

        return `${pathname}?${params.toString()}`;
    }, []);

    const onCompletedChatMessage = useCallback(
        (payload) => {
            const completedMessageId = payload.id || payload.message_id;
            if (!completedMessageId || notifiedMessageIds.current.has(completedMessageId)) return;

            notifiedMessageIds.current.add(completedMessageId);

            const assignmentId = payload.assignment_id;
            const isChatAlreadyOpen =
                auth?.role === 'student' &&
                Boolean(assignmentId) &&
                location.pathname.includes('/submissions/') &&
                openAssignmentChatId === assignmentId;
            if (isChatAlreadyOpen) return;

            const submissionPath = auth?.role === 'student' ? submissionPathByAssignmentId[assignmentId] : null;
            const nextPath =
                submissionPath || (assignmentId ? `/assignments/${assignmentId}/submissions` : location.pathname);

            message.success({
                text: t('message.new-response-ready', 'New response is ready'),
                action: (
                    <Button
                        sx={{ mr: 1 }}
                        color={'neutral'}
                        size={'large'}
                        variant={'borderless'}
                        onClick={() => {
                            close();
                            history.push(getOpenChatPath(nextPath));
                        }}
                    >
                        {t('controls.open', 'Open')}
                    </Button>
                ),
            });
        },
        [
            t,
            auth?.role,
            close,
            getOpenChatPath,
            history,
            location.pathname,
            message,
            openAssignmentChatId,
            submissionPathByAssignmentId,
        ]
    );

    useCompletedChatMessage(onCompletedChatMessage);

    return <ChatCoordinatorContext.Provider value={value}>{children}</ChatCoordinatorContext.Provider>;
};

const useChatCoordinator = () => useContext(ChatCoordinatorContext);

export { useChatCoordinator };
export default ChatCoordinator;
